import { LoginType, SocialType } from "../../_genApi/gameDeploy";
import { LoggedInUser } from "./auth-type-def";
import { UserInfoDto } from "src/_genApi/bco/models/UserInfoDto";
import { Cache, CacheUtil } from "./cache-util";
import ProcessEnvMgr from "../ProcessEnvMgr";
import GuestAuthModule from "./GuestAuthModule";
import SocialAuthModule from "./SocialAuthModule";
import AbstractAuthModule from "./AbstractAuthModule";
import CommonUtil from "../util/CommonUtil";
import { AUTH_TOKEN_KEY, AUTH_USER_KEY } from "./auth-api";

let _currentUser: LoggedInUser | undefined;

class _Auth {
  userInfoCache: Cache<UserInfoDto>;

  _socialLoginMethodNames = undefined;

  isLoginGuest() {
    return this._loginType === LoginType.GUEST;
  }

  private _loginType: LoginType;

  get loginType() {
    return this._loginType;
  }

  private authModule: AbstractAuthModule;

  constructor() {
    this.initAsync();
  }

  private _hasCachedSocialLogin(): boolean {
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      if (!token) return false;
      const userJson = localStorage.getItem(AUTH_USER_KEY);
      if (!userJson) return false;
      const user = JSON.parse(userJson);
      // If loginType is explicitly guest, it's not a social login
      if (user.loginType === LoginType.GUEST) return false;
      return true;
    } catch {
      return false;
    }
  }

  private _initAuthModule(loginType: LoginType) {
    if (loginType === LoginType.GUEST) {
      this.authModule = new GuestAuthModule();
    } else {
      this.authModule = new SocialAuthModule();
    }
    this.authModule.setCurrentUser = this.setCurrentUser;
    this.authModule.getCurrentUser = this.getCurrentUser;
  }

  async initAsync() {
    this.userInfoCache = CacheUtil.create<UserInfoDto>('UserInfoCache');

    const loginType = this._hasCachedSocialLogin() ? LoginType.SOCIAL : LoginType.GUEST;
    this._loginType = loginType;

    this._initAuthModule(loginType);
    this.authModule.start();
  }

  getCurrentUser() {
    return _currentUser;
  }

  setCurrentUser(whatever) {
    _currentUser = whatever;
  }


  async getCurrentUserInfo(): Promise<LoggedInUser | undefined> {
    return _currentUser;
  }

  isUnauthorizedError(err: Error) {
    return (err as any)?.status === 401;
  }

  async userReLogin(socialType: SocialType): Promise<void> {
    // Switch to SocialAuthModule if currently guest
    if (this._loginType === LoginType.GUEST) {
      this._loginType = LoginType.SOCIAL;
      this._initAuthModule(LoginType.SOCIAL);
    }
    await this.authModule.userReLogin(socialType);
  }

  async loadToken(socialType: SocialType, authenCode: string) {
    // Ensure we're using SocialAuthModule for token loading
    if (this._loginType === LoginType.GUEST) {
      this._loginType = LoginType.SOCIAL;
      this._initAuthModule(LoginType.SOCIAL);
    }
    await this.authModule.loadToken(socialType, authenCode);
  }

  private onLoggedIn() {
    return this.authModule.onLoggedIn();
  }

  hadLoggedIn(): boolean {
    return this.authModule?.hadLoggedIn();
  }

  public logout() {
    this.authModule.logout();
    // Switch back to guest mode after logout
    this._loginType = LoginType.GUEST;
    this._initAuthModule(LoginType.GUEST);
    this.authModule.start();
  }

  async getUserInfoById(authorId: number): Promise<UserInfoDto | undefined> {
    let info = this.userInfoCache.get(authorId);
    if (this.userInfoCache.has(authorId)) return info;


    // DEPRECATED: load info use from BCO
    // info = await Network.withServerBCO().getUserInfo(authorId);
    info = undefined;

    this.userInfoCache.set(authorId, info);
    return info;
  }

  getLoginToken() {
    return this.authModule.getLoginToken();
  }

  get socialLoginMethodNames(): string[] {
    if (!this._socialLoginMethodNames) {
      this._socialLoginMethodNames = [];

      if (ProcessEnvMgr.useGGLogin())
        this._socialLoginMethodNames.push(SocialType.GOOGLE)

      if (ProcessEnvMgr.useFBLogin())
        this._socialLoginMethodNames.push(SocialType.FACEBOOK)

      if (ProcessEnvMgr.useAppleLogin())
        this._socialLoginMethodNames.push(SocialType.APPLE)

      if (ProcessEnvMgr.useZACCLogin())
        this._socialLoginMethodNames.push(SocialType.ZACC)
    }
    return this._socialLoginMethodNames;
  }

  getUsername() {
    return this.authModule?.getUsername() || "";
  }
}

const Auth = new _Auth();
export default Auth;
